import * as React from 'react';
import { Check, ChevronsUpDown, Search, User, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Resource } from '@/types';

interface EnhancedResourceSelectProps {
  resources: Resource[];
  selectedResourceId?: string;
  onSelect: (resourceId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  calculateUtilization?: (resourceId: string) => number;
  className?: string;
}

interface ResourceWithUtilization extends Resource {
  utilization?: number;
}

export function EnhancedResourceSelect({
  resources,
  selectedResourceId,
  onSelect,
  disabled = false,
  placeholder = 'Select resource...',
  calculateUtilization,
  className,
}: EnhancedResourceSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  const selectedResource = resources.find((r) => String(r.id) === String(selectedResourceId));
  const selectedResourceUtilization = selectedResource && calculateUtilization 
    ? calculateUtilization(selectedResource.id) 
    : undefined;

  // Get utilization color based on percentage (using EDS accent colors)
  const getUtilizationColor = (utilization: number): string => {
    if (utilization >= 90) return 'bg-accent-destructive-500';
    if (utilization >= 70) return 'bg-accent-warning-500';
    return 'bg-accent-success-500';
  };

  // Get utilization label
  const getUtilizationLabel = (utilization: number): string => {
    if (utilization >= 90) return 'High';
    if (utilization >= 70) return 'Moderate';
    return 'Available';
  };

  // Get utilization status for badge
  const getUtilizationStatus = (utilization: number): 'default' | 'secondary' | 'destructive' => {
    if (utilization >= 90) return 'destructive';
    if (utilization >= 70) return 'secondary';
    return 'default';
  };

  // Enhance resources with utilization data
  const resourcesWithUtilization: ResourceWithUtilization[] = React.useMemo(() => {
    return resources.map((resource) => ({
      ...resource,
      utilization: calculateUtilization ? calculateUtilization(resource.id) : undefined,
    }));
  }, [resources, calculateUtilization]);

  // Filter resources based on search query
  const filteredResources = React.useMemo(() => {
    if (!searchQuery) return resourcesWithUtilization;

    const query = searchQuery.toLowerCase();
    return resourcesWithUtilization.filter(
      (resource) =>
        resource.name.toLowerCase().includes(query) ||
        resource.role.toLowerCase().includes(query)
    );
  }, [resourcesWithUtilization, searchQuery]);

  // Sort resources by utilization (available first)
  const sortedResources = React.useMemo(() => {
    return [...filteredResources].sort((a, b) => {
      if (a.utilization === undefined && b.utilization === undefined) return 0;
      if (a.utilization === undefined) return 1;
      if (b.utilization === undefined) return -1;
      return a.utilization - b.utilization;
    });
  }, [filteredResources]);

  const handleSelect = (resourceId: string) => {
    onSelect(resourceId);
    setOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect('');
  };

  // Get initials for avatar
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between h-auto min-h-[60px] px-3 py-2',
            !selectedResource && 'text-muted-foreground',
            className
          )}
        >
          {selectedResource ? (
            <div className="flex items-center gap-3 w-full">
              {/* Avatar */}
              <Avatar className="h-10 w-10 rounded-md shrink-0">
                <AvatarImage src={selectedResource.photoUrl} alt={selectedResource.name} />
                <AvatarFallback className="bg-primary/10 text-primary font-semibold rounded-md">
                  {getInitials(selectedResource.name)}
                </AvatarFallback>
              </Avatar>

              {/* Resource Info */}
              <div className="flex flex-col items-start flex-1 min-w-0">
                <div className="flex items-center gap-2 w-full">
                  <span className="font-medium text-sm truncate">
                    {selectedResource.name}
                  </span>
                  {selectedResourceUtilization !== undefined && (
                    <Badge
                      variant={getUtilizationStatus(selectedResourceUtilization)}
                      className="text-xs shrink-0"
                    >
                      {selectedResourceUtilization}%
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 w-full">
                  <span className="text-xs text-muted-foreground truncate">
                    {selectedResource.role}
                  </span>
                </div>

                {/* Utilization Bar */}
                {selectedResourceUtilization !== undefined && (
                  <div className="w-full mt-1.5">
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full transition-all duration-300',
                          getUtilizationColor(selectedResourceUtilization)
                        )}
                        style={{ width: `${Math.min(selectedResourceUtilization, 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Clear button */}
              <X
                className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100 transition-opacity"
                onClick={handleClear}
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 opacity-50" />
              <span>{placeholder}</span>
            </div>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[500px] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              placeholder="Search by name or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <CommandList className="max-h-[400px]">
            <CommandEmpty>
              <div className="py-6 text-center">
                <User className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">No resources found</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Try adjusting your search
                </p>
              </div>
            </CommandEmpty>
            <CommandGroup>
              {sortedResources.map((resource) => (
                <CommandItem
                  key={resource.id}
                  value={resource.id}
                  onSelect={() => handleSelect(resource.id)}
                  className="px-3 py-3 cursor-pointer"
                >
                  <div className="flex items-center gap-3 w-full">
                    {/* Avatar */}
                    <Avatar className="h-10 w-10 rounded-md shrink-0">
                      <AvatarImage src={resource.photoUrl} alt={resource.name} />
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs rounded-md">
                        {getInitials(resource.name)}
                      </AvatarFallback>
                    </Avatar>

                    {/* Resource Info */}
                    <div className="flex flex-col flex-1 min-w-0">
                      <div className="flex items-center gap-2 w-full mb-1">
                        <span className="font-medium text-sm truncate">
                          {resource.name}
                        </span>
                        {resource.utilization !== undefined && (
                          <Badge
                            variant={getUtilizationStatus(resource.utilization)}
                            className="text-xs shrink-0"
                          >
                            {resource.utilization}%
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground truncate mb-1.5">
                        {resource.role}
                      </span>

                      {/* Utilization Bar */}
                      {resource.utilization !== undefined && (
                        <div className="w-full">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-muted-foreground">
                              {getUtilizationLabel(resource.utilization)}
                            </span>
                          </div>
                          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className={cn(
                                'h-full transition-all duration-300',
                                getUtilizationColor(resource.utilization)
                              )}
                              style={{ width: `${Math.min(resource.utilization, 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Check icon for selected */}
                    <Check
                      className={cn(
                        'h-4 w-4 shrink-0',
                        String(selectedResourceId) === String(resource.id) ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Compact version for smaller spaces
interface CompactResourceSelectProps extends EnhancedResourceSelectProps {
  showUtilizationBar?: boolean;
}

export function CompactResourceSelect({
  resources,
  selectedResourceId,
  onSelect,
  disabled = false,
  placeholder = 'Select resource...',
  calculateUtilization,
  showUtilizationBar = true,
  className,
}: CompactResourceSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  const selectedResource = resources.find((r) => String(r.id) === String(selectedResourceId));
  const selectedResourceUtilization = selectedResource && calculateUtilization 
    ? calculateUtilization(selectedResource.id) 
    : undefined;

  const getUtilizationColor = (utilization: number): string => {
    if (utilization >= 90) return 'bg-accent-destructive-500';
    if (utilization >= 70) return 'bg-accent-warning-500';
    return 'bg-accent-success-500';
  };

  const getUtilizationStatus = (utilization: number): 'default' | 'secondary' | 'destructive' => {
    if (utilization >= 90) return 'destructive';
    if (utilization >= 70) return 'secondary';
    return 'default';
  };

  const resourcesWithUtilization: ResourceWithUtilization[] = React.useMemo(() => {
    return resources.map((resource) => ({
      ...resource,
      utilization: calculateUtilization ? calculateUtilization(resource.id) : undefined,
    }));
  }, [resources, calculateUtilization]);

  const filteredResources = React.useMemo(() => {
    if (!searchQuery) return resourcesWithUtilization;

    const query = searchQuery.toLowerCase();
    return resourcesWithUtilization.filter(
      (resource) =>
        resource.name.toLowerCase().includes(query) ||
        resource.role.toLowerCase().includes(query)
    );
  }, [resourcesWithUtilization, searchQuery]);

  const sortedResources = React.useMemo(() => {
    return [...filteredResources].sort((a, b) => {
      if (a.utilization === undefined && b.utilization === undefined) return 0;
      if (a.utilization === undefined) return 1;
      if (b.utilization === undefined) return -1;
      return a.utilization - b.utilization;
    });
  }, [filteredResources]);

  const handleSelect = (resourceId: string) => {
    onSelect(resourceId);
    setOpen(false);
    setSearchQuery('');
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between h-auto min-h-[48px] px-3 py-2',
            !selectedResource && 'text-muted-foreground',
            className
          )}
        >
          {selectedResource ? (
            <div className="flex items-center gap-2 w-full">
              <Avatar className="h-8 w-8 rounded-md shrink-0">
                <AvatarImage src={selectedResource.photoUrl} alt={selectedResource.name} />
                <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs rounded-md">
                  {getInitials(selectedResource.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col items-start flex-1 min-w-0">
                <span className="font-medium text-sm truncate w-full">
                  {selectedResource.name}
                </span>
                <span className="text-xs text-muted-foreground truncate w-full">
                  {selectedResource.role}
                </span>
              </div>
              {selectedResourceUtilization !== undefined && (
                <Badge
                  variant={getUtilizationStatus(selectedResourceUtilization)}
                  className="text-xs shrink-0"
                >
                  {selectedResourceUtilization}%
                </Badge>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 opacity-50" />
              <span>{placeholder}</span>
            </div>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              placeholder="Search by name or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <CommandList className="max-h-[350px]">
            <CommandEmpty>
              <div className="py-6 text-center">
                <User className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">No resources found</p>
              </div>
            </CommandEmpty>
            <CommandGroup>
              {sortedResources.map((resource) => (
                <CommandItem
                  key={resource.id}
                  value={resource.id}
                  onSelect={() => handleSelect(resource.id)}
                  className="px-3 py-2 cursor-pointer"
                >
                  <div className="flex items-center gap-2 w-full">
                    <Avatar className="h-8 w-8 rounded-md shrink-0">
                      <AvatarImage src={resource.photoUrl} alt={resource.name} />
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs rounded-md">
                        {getInitials(resource.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col flex-1 min-w-0">
                      <div className="flex items-center gap-2 w-full">
                        <span className="font-medium text-sm truncate">
                          {resource.name}
                        </span>
                        {resource.utilization !== undefined && (
                          <Badge
                            variant={getUtilizationStatus(resource.utilization)}
                            className="text-xs shrink-0"
                          >
                            {resource.utilization}%
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground truncate">
                        {resource.role}
                      </span>
                      {showUtilizationBar && resource.utilization !== undefined && (
                        <div className="w-full mt-1">
                          <div className="h-1 bg-muted rounded-full overflow-hidden">
                            <div
                              className={cn(
                                'h-full transition-all duration-300',
                                getUtilizationColor(resource.utilization)
                              )}
                              style={{ width: `${Math.min(resource.utilization, 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <Check
                      className={cn(
                        'h-4 w-4 shrink-0',
                        String(selectedResourceId) === String(resource.id) ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
